import React, { useState, useEffect, useCallback, useRef } from "react";
import './Wishlist.css';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrash, FaShoppingCart } from 'react-icons/fa';
import Header from "./Header";
import mapy from './icon/mapy.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-f7ce.up.railway.app';

// ── Decode userId from JWT — no network round-trip needed ─────────────────────
function userIdFromToken(token) {
    try {
        if (!token) return null;
        const pl = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return pl.user_id || pl.sub || pl.id || null;
    } catch { return null; }
}

// ── sessionStorage cache ──────────────────────────────────────────────────────
const CACHE_TTL = 2 * 60 * 1000; // 2 min — wishlist changes often
const ssGet = (k) => { try { const r = sessionStorage.getItem(k); if (!r) return null; const p = JSON.parse(r); return Date.now() < p.exp ? p.v : null; } catch { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify({ v, exp: Date.now() + CACHE_TTL })); } catch {} };

function Wishlist() {
    const navigate       = useNavigate();
    const token          = localStorage.getItem('token');
    const popupTimeoutRef = useRef(null);

    // ── Get userId instantly from JWT or localStorage — no fetch needed ───────
    const [userId, setUserId] = useState(() => {
        return userIdFromToken(token) || localStorage.getItem('userId') || null;
    });

    // ── Load cached wishlist immediately for instant first paint ──────────────
    const cachedItems = userId ? ssGet(`wishlist:${userId}`) : null;
    const [wishlistItems,      setWishlistItems]      = useState(cachedItems || []);
    const [loading,            setLoading]            = useState(!cachedItems);
    const [error,              setError]              = useState(null);
    const [popupMessage,       setPopupMessage]       = useState("");
    const [popupType,          setPopupType]          = useState("success");
    const [itemPendingRemoval, setItemPendingRemoval] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';


    const showPopup = (msg, type = "success") => {
        clearTimeout(popupTimeoutRef.current);
        setPopupType(type);
        setPopupMessage(msg);
        popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);
    };

    // ── Fetch wishlist ────────────────────────────────────────────────────────
    const fetchWishlist = useCallback(async (uid, background = false) => {
        if (!uid) { setError("Please log in to view your wishlist."); setLoading(false); return; }
        if (!background) setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/api/wishlist/${uid}`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) { const e = await res.json(); setError(e.error || res.statusText); return; }
            const data = await res.json();
            setWishlistItems(data);
            ssSet(`wishlist:${uid}`, data);
            localStorage.setItem("wishlist", JSON.stringify(data));
        } catch { setError("Error fetching wishlist."); }
        finally { setLoading(false); }
    }, [token]);

    useEffect(() => {
        if (!token) { setLoading(false); setError("Please log in."); return; }

        if (userId) {
            // Already have userId — fetch wishlist immediately
            // If cached, this is a background refresh
            fetchWishlist(userId, !!cachedItems);
        } else {
            // Last resort: hit the server for userId, then fetch wishlist
            fetch(`${API_URL}/api/get-user-id`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then(r => r.ok ? r.json() : Promise.reject())
                .then(data => {
                    const uid = data.user_id;
                    setUserId(uid);
                    localStorage.setItem('userId', uid);
                    fetchWishlist(uid);
                })
                .catch(() => { setError("Could not fetch user info."); setLoading(false); });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Remove item — optimistic ──────────────────────────────────────────────
    const actuallyRemoveFromWishlist = async (wishlistId) => {
        const updated = wishlistItems.filter(i => i.id !== wishlistId);
        setWishlistItems(updated); // optimistic
        ssSet(`wishlist:${userId}`, updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
        try {
            const res = await fetch(`${API_URL}/api/wishlist/item/${wishlistId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                // Revert on failure
                fetchWishlist(userId);
            }
        } catch { fetchWishlist(userId); }
    };

    // ── Add to cart ───────────────────────────────────────────────────────────
    const handleAddToCartClick = async (card) => {
        const tok = localStorage.getItem('token');
        const uid = localStorage.getItem('userId');
        if (!tok || !uid) { showPopup("Please log in to add items to your cart.", "error"); return; }
        try {
            const res = await fetch(`${API_URL}/api/cart_items`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ item_id: card.item_id, product_name: card.name, quantity: 1, user_id: uid }),
            });
            if (!res.ok) { const e = await res.json(); showPopup(e.error || "Failed to add to cart.", "error"); }
            else showPopup("Added to cart!", "success");
        } catch { showPopup("Failed to add to cart.", "error"); }
    };

    useEffect(() => () => clearTimeout(popupTimeoutRef.current), []);

    return (
        <>
            <Header />
            <div className="wl-page">

                {/* ── Top bar ── */}
                <div className="wl-topbar">
                    <button className="wl-back-btn" onClick={() => navigate('/Shop')}>
                        <FaArrowLeft /> Back
                    </button>
                    <span className="wl-topbar-title">Wishlist</span>
                    {!loading && (
                        <span className="wl-topbar-count">
                            {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}
                        </span>
                    )}
                </div>

                {/* ── Content ── */}
                <div className="wl-content">
                    {loading ? (
                        <div className="wl-spinner-wrap">
                            <div className="wl-spinner" />
                            <p className="wl-spinner-text">Loading your wishlist…</p>
                        </div>
                    ) : error ? (
                        <div className="wl-error">{error}</div>
                    ) : wishlistItems.length === 0 ? (
                        <div className="wl-empty">
                            <div className="wl-empty-icon">🤍</div>
                            <h3>Your wishlist is empty</h3>
                            <p>Save items you love and come back to them any time.</p>
                            <button className="wl-empty-cta" onClick={() => navigate('/Shop')}>
                                Browse Shop
                            </button>
                        </div>
                    ) : (
                        <div className="wl-grid">
                            {wishlistItems.map((item, idx) => (
                                <div
                                    key={item.item_id}
                                    className="wl-card"
                                    style={{ animationDelay: `${idx * 0.07}s` }}
                                >
                                    <div className="wl-card-img-wrap">
                                        <img
                                            src={item.image_url}
                                            alt={item.name}
                                            className="wl-card-img"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>

                                    <div className="wl-card-body">
                                        <h3 className="wl-card-name">{item.name}</h3>
                                        {item.description && (
                                            <p className="wl-card-subtitle">{item.description}</p>
                                        )}
                                        <p className="wl-card-category">{item.page_name}</p>
                                        {item.slocation && (
                                            <p className="wl-card-location">
                                                <img src={mapy} width="11" height="11" alt="" />
                                                {item.slocation}
                                            </p>
                                        )}

                                        <div className="wl-card-divider" />

                                        <p className="wl-card-price">₹{item.price}</p>
                                        {item.discount > 0 && (
                                            <p className="wl-card-discount-text">({item.discount}% off)</p>
                                        )}
                                        {item.mrp && (
                                            <p className="wl-card-mrp">M.R.P.: ₹{item.mrp}</p>
                                        )}

                                        <button
                                            className="wl-card-cart-btn"
                                            onClick={() => handleAddToCartClick(item)}
                                        >
                                            <FaShoppingCart /> Add to Cart
                                        </button>

                                        <button
                                            className="wl-card-remove-btn"
                                            onClick={() => setItemPendingRemoval(item.id)}
                                        >
                                            <FaTrash /> Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Remove confirmation modal ── */}
            {itemPendingRemoval && (
                <div className="wl-modal-overlay">
                    <div className="wl-modal">
                        <div className="wl-modal-icon">🗑️</div>
                        <h3>Remove item?</h3>
                        <p>This item will be removed from your wishlist.</p>
                        <div className="wl-modal-btns">
                            <button
                                className="wl-modal-confirm"
                                onClick={() => { actuallyRemoveFromWishlist(itemPendingRemoval); setItemPendingRemoval(null); }}
                            >
                                Yes, Remove
                            </button>
                            <button
                                className="wl-modal-cancel"
                                onClick={() => setItemPendingRemoval(null)}
                            >
                                Keep it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Toast ── */}
            {popupMessage && (
                <div className={`wl-toast wl-toast--${popupType}`}>
                    <span>{popupMessage}</span>
                    {popupType === "success" && (
                        <button className="wl-toast-cart-btn" onClick={() => navigate('/Cart')}>
                            Go to Cart →
                        </button>
                    )}
                </div>
            )}
        </>
    );
}

export default Wishlist;